import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Bell, HelpCircle, Phone, LogOut, Mail, Clock, ShieldAlert, BookOpen, Info, MapPin, LayoutDashboard, Plane, Hotel, CalendarDays, ExternalLink, ChevronRight } from "lucide-react";
import { fmtDateLong } from "@/lib/format";

// Short, attendee-friendly section labels for the KBYG mobile experience.
const SECTION_SHORT = {
  "About President's Club": "About",
  "Your Event Dates": "Dates",
  "Destination & Arrival": "Destination",
  "Passport, Visa & Entry": "Entry",
  "Your Travel & Guest Policy": "Travel & guests",
  "Extensions & Extra Nights": "Extra nights",
  "Room & Guest Rules": "Room rules",
  "What's Included & Not Included": "Included",
  "FAQs": "FAQs",
  "Official Travel References & Help": "Help",
};

export default function More() {
  const { eventId, attendeeId, event, isManager, dualAccess, signOut, demoMode } = useApp();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [kbyg, setKbyg] = useState({ categories: [] });
  const [anns, setAnns] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [questOn, setQuestOn] = useState(false);
  const openAnnId = sp.get("ann");
  const tab = sp.get("tab") || "kbyg";
  const setTab = (t) => { const n = new URLSearchParams(sp); n.set("tab", t); n.delete("ann"); setSp(n, { replace: true }); };

  useEffect(() => {
    api.kbyg(eventId, attendeeId).then(setKbyg).catch(() => {});
    api.announcements(eventId, attendeeId).then(setAnns).catch(() => {});
    api.faqs(eventId).then(setFaqs).catch(() => {});
    api.contacts(eventId).then(setContacts).catch(() => {});
    api.questVisible(eventId).then((r) => setQuestOn(!!r.visible)).catch(() => {});
  }, [eventId, attendeeId]);

  const travelContact = contacts.find((c) => (c.type || "").toLowerCase().includes("travel")) || contacts[0];
  const essentials = [
    { icon: CalendarDays, label: "Event dates", value: event?.date_label },
    { icon: MapPin, label: "Destination", value: event?.destination },
    { icon: Plane, label: "Airport", value: event?.destination_airport || "Los Cabos Intl (SJD)" },
    { icon: Hotel, label: "Check-in / out", value: "3:00 PM / 11:00 AM" },
  ].filter((e) => e.value);

  return (
    <div className="pt-16 px-4 pb-6" data-testid="attendee-more-menu">
      <div className="pt-4 pb-3">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Information</span>
        <h1 className="font-serif text-4xl text-cream">More</h1>
      </div>

      <button
        data-testid="more-my-profile"
        onClick={() => navigate("/app/profile")}
        className="mb-4 w-full att-card p-3.5 flex items-center justify-between focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]"><Info className="h-5 w-5" /></div>
          <div className="text-left">
            <div className="font-semibold text-cream">My Profile</div>
            <div className="text-xs text-cream-dim">Photo, display name &amp; birthday</div>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
      </button>

      {questOn && (
        <button
          data-testid="more-pc-quest"
          onClick={() => navigate("/app/quest")}
          className="mb-4 w-full att-card p-3.5 flex items-center justify-between focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(var(--pc-gold))] border border-[hsl(var(--pc-gold)/0.3)]"><HelpCircle className="h-5 w-5" /></div>
            <div className="text-left">
              <div className="font-semibold text-cream">PC Quest</div>
              <div className="text-xs text-cream-dim">Four categories · twenty questions</div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--pc-gold))]" />
        </button>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-4 att-glass rounded-full p-1 mb-4">
          <TabsTrigger value="kbyg" className="rounded-full text-cream data-[state=active]:bg-[hsl(var(--pc-gold))] data-[state=active]:text-[hsl(var(--pc-ink))] text-[11px]">Know</TabsTrigger>
          <TabsTrigger value="announcements" className="rounded-full text-cream data-[state=active]:bg-[hsl(var(--pc-gold))] data-[state=active]:text-[hsl(var(--pc-ink))] text-[11px]">Updates</TabsTrigger>
          <TabsTrigger value="faqs" className="rounded-full text-cream data-[state=active]:bg-[hsl(var(--pc-gold))] data-[state=active]:text-[hsl(var(--pc-ink))] text-[11px]">FAQs</TabsTrigger>
          <TabsTrigger value="contacts" className="rounded-full text-cream data-[state=active]:bg-[hsl(var(--pc-gold))] data-[state=active]:text-[hsl(var(--pc-ink))] text-[11px]">Contacts</TabsTrigger>
        </TabsList>

        {/* KNOW BEFORE YOU GO — scannable Quick Essentials + expandable sections */}
        <TabsContent value="kbyg" className="mt-0 space-y-4" data-testid="attendee-kbyg">
          {essentials.length > 0 && (
            <div className="att-card p-4" data-testid="kbyg-quick-essentials">
              <div className="flex items-center gap-2 text-[hsl(var(--pc-gold))] mb-3"><BookOpen className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Quick Essentials</span></div>
              <div className="grid grid-cols-2 gap-3">
                {essentials.map((e) => {
                  const Icon = e.icon;
                  return (
                    <div key={e.label} className="flex items-start gap-2">
                      <Icon className="h-4 w-4 text-[hsl(var(--pc-gold))] mt-0.5 shrink-0" />
                      <div className="min-w-0"><div className="text-[10px] uppercase tracking-wide text-cream-dim">{e.label}</div><div className="text-sm text-cream font-medium truncate">{e.value}</div></div>
                    </div>
                  );
                })}
              </div>
              {travelContact?.phone && (
                <a href={`tel:${String(travelContact.phone).replace(/[^0-9+]/g, "")}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]"><Phone className="h-3.5 w-3.5" /> Travel Help: {travelContact.phone}</a>
              )}
            </div>
          )}

          {kbyg.categories?.map((cat) => (
            <div key={cat.category} data-testid={`kbyg-cat-${cat.category}`}>
              <h3 className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[hsl(var(--pc-gold))] mb-2 px-1">{SECTION_SHORT[cat.category] || cat.category}</h3>
              <Accordion type="single" collapsible className="att-card px-4 divide-y divide-[hsl(var(--pc-line))]">
                {cat.items.map((i) => (
                  <AccordionItem key={i.id} value={i.id} className="border-none">
                    <AccordionTrigger className="text-left text-sm font-semibold text-cream hover:no-underline py-3.5">{i.title}</AccordionTrigger>
                    <AccordionContent className="text-sm text-cream-dim leading-relaxed whitespace-pre-line pb-4">
                      {i.content}
                      {i.cta_label && i.cta_url && (
                        <a href={i.cta_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]">{i.cta_label} <ExternalLink className="h-3.5 w-3.5" /></a>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
          {(!kbyg.categories || kbyg.categories.length === 0) && <div className="att-card p-6 text-center text-sm text-cream-dim">Your guide will appear here soon.</div>}
        </TabsContent>

        {/* ANNOUNCEMENTS — clickable detail */}
        <TabsContent value="announcements" className="space-y-3 mt-0" data-testid="attendee-announcements">
          {anns.length === 0 && <div className="att-card p-6 text-center text-sm text-cream-dim">No announcements yet.</div>}
          {anns.map((a) => {
            const open = openAnnId === a.id;
            const important = a.priority === "high" || a.priority === "important";
            return (
              <button
                key={a.id}
                data-testid={`attendee-announcement-${a.id}`}
                onClick={() => { const n = new URLSearchParams(sp); n.set("tab", "announcements"); if (open) n.delete("ann"); else n.set("ann", a.id); setSp(n, { replace: true }); }}
                className={`w-full text-left att-card p-4 transition-colors ${a.pinned ? "border-[hsl(var(--pc-gold)/0.45)]" : ""}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0"><Bell className={`h-4 w-4 shrink-0 ${important ? "text-[hsl(var(--pc-gold))]" : "text-[hsl(var(--mint))]"}`} /><div className="font-semibold text-cream truncate">{a.title}</div></div>
                  <ChevronRight className={`h-4 w-4 text-cream-dim shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
                </div>
                <p className={`text-sm text-cream-dim mt-1.5 ${open ? "" : "line-clamp-2"}`}>{a.body}</p>
                {open && a.cta_label && a.cta_url && (
                  <a href={a.cta_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--pc-gold))]">{a.cta_label} <ExternalLink className="h-3.5 w-3.5" /></a>
                )}
                <div className="text-[11px] text-cream-dim mt-2">{fmtDateLong(a.publish_at || a.published_at)}</div>
              </button>
            );
          })}
        </TabsContent>

        {/* FAQs */}
        <TabsContent value="faqs" className="mt-0">
          <Accordion type="single" collapsible className="att-card px-4 divide-y divide-[hsl(var(--pc-line))]">
            {faqs.map((f) => (
              <AccordionItem key={f.id} value={f.id} className="border-none">
                <AccordionTrigger className="text-left text-sm font-semibold text-cream hover:no-underline">
                  <span className="flex items-center gap-2"><HelpCircle className="h-4 w-4 text-[hsl(var(--pc-gold))] shrink-0" />{f.question}</span>
                </AccordionTrigger>
                <AccordionContent className="text-sm text-cream-dim">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* CONTACTS */}
        <TabsContent value="contacts" className="space-y-3 mt-0">
          {contacts.map((c) => (
            <div key={c.id} className="att-card p-4">
              <div className="flex items-center gap-2">{c.type === "emergency" ? <ShieldAlert className="h-4 w-4 text-[hsl(0_70%_70%)]" /> : <Phone className="h-4 w-4 text-[hsl(var(--pc-gold))]" />}<div className="font-semibold text-cream">{c.name}</div></div>
              <div className="text-xs text-cream-dim mt-0.5">{c.role}</div>
              <div className="mt-2 space-y-1 text-sm">
                <a href={`tel:${String(c.phone).replace(/[^0-9+]/g, "")}`} className="flex items-center gap-2 text-cream"><Phone className="h-4 w-4" /> {c.phone}</a>
                {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-cream"><Mail className="h-4 w-4" /> {c.email}</a>}
                {c.available && <div className="flex items-center gap-2 text-cream-dim"><Clock className="h-4 w-4" /> {c.available}</div>}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {/* About */}
      <div className="mt-6 att-card p-4">
        <div className="flex items-center gap-2 text-[hsl(var(--pc-gold))]"><Info className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">About President's Club</span></div>
        <p className="text-sm text-cream-dim mt-2 leading-relaxed">President's Club is Collabera's premier recognition celebration for our highest performers. {event?.name} takes place at {event?.host_resort} in {event?.destination}.</p>
        <div className="mt-2 flex items-center gap-2 text-xs text-cream-dim"><MapPin className="h-3.5 w-3.5" /> {event?.date_label}</div>
      </div>

      {(dualAccess || (demoMode && isManager)) && (
        <button data-testid="attendee-open-events-manager" onClick={() => navigate("/manager")} className="mt-6 w-full flex items-center justify-center gap-2 rounded-2xl bg-[hsl(var(--pc-gold))] py-3 text-sm font-semibold text-[hsl(var(--pc-ink))] shadow-lg hover:brightness-105 transition-[filter]">
          <LayoutDashboard className="h-4 w-4" /> Switch to Events Manager
        </button>
      )}

      {!demoMode && (
        <button data-testid="attendee-signout-button" onClick={signOut} className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl att-glass py-3 text-sm font-semibold text-cream hover:bg-[hsl(var(--pc-surface2)/0.7)] transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      )}
      <div className="mt-4 text-center text-[11px] text-cream-dim">Collabera Events · President's Club 2026</div>
    </div>
  );
}
